import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  MapPin,
  MessageSquareText,
  Plus,
  Trash2,
  Send,
  AlertTriangle,
} from "lucide-react";
import WeeklySlotsEditor, { emptySlots } from "@/components/autoescuela/WeeklySlotsEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 30000 });

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AvailabilityQuestionnairePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("pending");
  const [studentName, setStudentName] = useState("");

  const [slots, setSlots] = useState(emptySlots());
  const [prefs, setPrefs] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [pickup, setPickup] = useState("");
  const [incidents, setIncidents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await publicApi.get(`/public/availability-questionnaire/${token}`);
      setStatus(data.status);
      setStudentName(data.student_name || "");
      const av = data.availability || {};
      setSlots(av.weekly_slots || emptySlots());
      setPrefs(av.preferences || "");
      setNeighborhood(av.neighborhood || "");
      setPickup(av.habitual_pickup_hint || "");
      setSubmittedAt(data.submitted_at || "");
    } catch (err) {
      setError(err?.response?.data?.detail || "El enlace no es válido o ha caducado.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const addIncident = () => setIncidents([...incidents, { reason: "", date_start: todayISO(), date_end: todayISO() }]);
  const updateIncident = (idx, field, val) => setIncidents(incidents.map((x, i) => (i === idx ? { ...x, [field]: val } : x)));
  const removeIncident = (idx) => setIncidents(incidents.filter((_, i) => i !== idx));

  const submit = async () => {
    for (const inc of incidents) {
      if (!inc.reason.trim() || !inc.date_start || !inc.date_end) {
        toast.error("Todas las incidencias necesitan motivo, fecha inicio y fecha fin.");
        return;
      }
      if (inc.date_end < inc.date_start) {
        toast.error(`Incidencia "${inc.reason}": la fecha fin es anterior a la de inicio.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await publicApi.post(`/public/availability-questionnaire/${token}/submit`, {
        weekly_slots: slots,
        preferences: prefs,
        neighborhood,
        habitual_pickup: pickup,
        incidents: incidents.map((i) => ({ reason: i.reason.trim(), date_start: i.date_start, date_end: i.date_end })),
      });
      setStatus("completed");
      setSubmittedAt(new Date().toISOString());
      toast.success("¡Gracias! Tus datos han sido enviados.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el formulario.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="questionnaire-error">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 mb-3">
            <AlertCircle className="h-3.5 w-3.5" /> Enlace no válido
          </div>
          <h1 className="text-2xl font-bold mb-3">No se puede abrir el cuestionario</h1>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  if (status === "completed" || status === "expired" || status === "cancelled") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4 py-10">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="questionnaire-done">
          <div className={"inline-flex items-center gap-2 rounded-full text-xs font-semibold px-3 py-1 mb-3 " + (status === "completed" ? "bg-emerald-100 text-emerald-800" : status === "cancelled" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800")}>
            {status === "completed" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            {status === "completed" ? "Formulario completado" : status === "cancelled" ? "Enlace cancelado" : "Enlace caducado"}
          </div>
          <h1 className="text-2xl font-bold mb-3">
            {status === "completed" ? `¡Gracias, ${studentName || "alumno"}!` : status === "cancelled" ? "Enlace cancelado" : "Enlace caducado"}
          </h1>
          <p className="text-neutral-600 leading-relaxed">
            {status === "completed"
              ? "Hemos recibido tu disponibilidad. Ya podemos planificar tus clases con la máxima flexibilidad."
              : status === "cancelled"
                ? "Este enlace ha sido cancelado por la autoescuela. Contacte con Natur Autoescuela para obtener uno nuevo."
                : "Este enlace ha caducado. Contacte con Natur Autoescuela para obtener uno nuevo."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10 space-y-4">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7">
          <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Natur Autoescuela · Planificador</div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight mt-1">
            Hola{studentName ? `, ${studentName}` : ""} 👋
          </h1>
          <p className="text-neutral-600 mt-2 leading-relaxed text-sm">
            Cuéntanos tu <b>disponibilidad</b> y posibles <b>incidencias</b> para que podamos programar tus clases prácticas
            de la mejor manera. Solo te llevará 2 minutos.
          </p>
        </div>

        {/* Weekly availability */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">¿Cuándo puedes hacer clases?</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-3">Añade las franjas horarias en las que estás disponible cada día.</p>
          <WeeklySlotsEditor value={slots} onChange={setSlots} testidPrefix="pub-slot" />
        </section>

        {/* Preferences */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7 space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquareText className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Preferencias</h2>
          </div>
          <textarea
            value={prefs}
            onChange={(e) => setPrefs(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="Ej: prefiero mañanas, los miércoles solo tarde, nunca sábados…"
            data-testid="pub-prefs"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">
                <MapPin className="inline h-3 w-3 mr-1" /> Tu barrio
              </label>
              <input
                type="text"
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
                className="w-full h-10 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Ej: Restón, Centro, Estación..."
                data-testid="pub-neighborhood"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Punto habitual de recogida</label>
              <input
                type="text"
                value={pickup}
                onChange={(e) => setPickup(e.target.value)}
                className="w-full h-10 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Ej: Plaza de España, Estación tren, mi casa..."
                data-testid="pub-pickup"
              />
            </div>
          </div>
        </section>

        {/* Incidents */}
        <section className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Incidencias temporales</h2>
            <button
              type="button"
              onClick={addIncident}
              className="ml-auto inline-flex items-center gap-1 h-8 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700 px-2.5 text-xs font-bold"
              data-testid="pub-incident-add"
            >
              <Plus className="h-3.5 w-3.5" /> Añadir
            </button>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Fechas en las que <b>no podrás</b> hacer clases (exámenes, vacaciones, viaje, baja médica, etc.). Con fecha inicio y fecha fin.
          </p>

          {incidents.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3 text-xs text-neutral-500 text-center">
              Sin incidencias — puedes dejarlo así si no tienes ninguna.
            </div>
          ) : (
            <div className="space-y-2">
              {incidents.map((inc, i) => (
                <div key={i} className="rounded-xl border border-neutral-200 bg-white p-3 space-y-2" data-testid={`pub-incident-${i}`}>
                  <input
                    type="text"
                    value={inc.reason}
                    onChange={(e) => updateIncident(i, "reason", e.target.value)}
                    placeholder="Motivo (obligatorio)"
                    className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid={`pub-incident-reason-${i}`}
                    maxLength={300}
                  />
                  <div className="grid grid-cols-2 gap-2 items-end">
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-neutral-500 mb-0.5">Desde</label>
                      <input type="date" value={inc.date_start} onChange={(e) => updateIncident(i, "date_start", e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm" data-testid={`pub-incident-start-${i}`} />
                    </div>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="block text-[10px] font-bold uppercase text-neutral-500 mb-0.5">Hasta</label>
                        <input type="date" value={inc.date_end} onChange={(e) => updateIncident(i, "date_end", e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm" data-testid={`pub-incident-end-${i}`} />
                      </div>
                      <button type="button" onClick={() => removeIncident(i)} className="h-9 w-9 flex items-center justify-center text-red-500 hover:text-red-700 rounded-lg border border-red-200 bg-white" data-testid={`pub-incident-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Submit */}
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="w-full h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-sm disabled:opacity-40 transition-colors"
          data-testid="pub-submit"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          Enviar mis datos
        </button>
      </div>
    </div>
  );
}
